import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { CustomBulkEditOptions } from 'lumen/components/settings/budget-forecast/modal-bulk-update/custom-bulk-edit';
import SettingsBudgetForecastModalBulkUpdateCustomBulkEdit from 'lumen/components/settings/budget-forecast/modal-bulk-update/custom-bulk-edit';
import type { FillFromExistingOptions } from 'lumen/components/settings/budget-forecast/modal-bulk-update/fill-from-existing';
import SettingsBudgetForecastModalBulkUpdateFillFromExisting from 'lumen/components/settings/budget-forecast/modal-bulk-update/fill-from-existing';
import SettingsBudgetForecastModalBulkUpdateMethodSelection from 'lumen/components/settings/budget-forecast/modal-bulk-update/method-selection';
import type {
  BudgetForecastDataItem,
  BudgetForecastEntry,
  BudgetForecastSegment,
} from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { applyBulkEditToItems } from 'lumen/utils/budget-forecast/bulk-edit';
import { markItemsAsNeedsForceDistribution } from 'lumen/utils/budget-forecast/force-distribution';
import type { DateGranularity } from 'lumen/utils/constants/budget-forecast-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './modal-bulk-update.module.css';

export type Method = 'fill-from-existing' | 'custom-bulk-edit';

const METHOD_TRANSLATIONS: Record<Method, string> = {
  'fill-from-existing': 'budget_forecast.bulk_update.fill_from_existing',
  'custom-bulk-edit': 'budget_forecast.bulk_update.custom_bulk_edit',
};

type Step = 'select-method' | 'fill-from-existing' | 'custom-bulk-edit';

type FillFromExistingQueryParams = {
  date_aggregation: string;
  end_date: string;
  entry_id: string;
  from_data_point: string;
  start_date: string;
  subscription_id: string;
};

interface SettingsBudgetForecastModalBulkUpdateSignature {
  Args: {
    data: BudgetForecastDataItem[];
    endDate: string;
    entry: BudgetForecastEntry;
    granularity: DateGranularity;
    onClose: () => void;
    segments: BudgetForecastSegment[];
    startDate: string;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastModalBulkUpdate extends Component<SettingsBudgetForecastModalBulkUpdateSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare budgetForecast: Services['budget-forecast'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked currentStep: Step = 'select-method';
  @tracked customBulkEditOptions: CustomBulkEditOptions = {
    metric: 'rooms',
    startDate: this.args.startDate,
    changeValue: null,
    changeType: 'percentage',
    endDate: this.args.endDate,
    selectedWeekdays: ['1', '2', '3', '4', '5', '6', '7'],
    segmentLevel: 'totals',
    selectedSegmentIds: [],
  };
  @tracked fillFromExistingOptions: FillFromExistingOptions = {
    startDate: this.args.startDate,
    endDate: this.args.endDate,
    dataSource: 'otb',
  };
  @tracked method: Method = 'fill-from-existing';

  fillFromExistingTask = task({ drop: true }, async (): Promise<void> => {
    const { startDate, endDate, dataSource } = this.fillFromExistingOptions;

    const baseUrl =
      this.args.view === 'budget'
        ? '/apigateway/v1/app/budgetforecast/budget/bulk-update/fill-from-existing'
        : '/apigateway/v1/app/budgetforecast/forecast/bulk-update/fill-from-existing';

    const queryParams: FillFromExistingQueryParams = {
      subscription_id: this.hotelAndMarketService.hotel.id!,
      start_date: startDate,
      end_date: endDate,
      from_data_point: dataSource,
      date_aggregation: this.args.granularity,
      entry_id: this.args.entry.id!,
    };

    const fillFromExistingUrl = formatUrl(baseUrl, queryParams, this.ajax.host);

    let updatedItems: BudgetForecastDataItem[];
    try {
      const response = await this.ajax.request<{ data: object[] }>(
        fillFromExistingUrl,
      );

      updatedItems = pushAndReturnPayload(
        this.store,
        response,
        'budget-forecast/data-item',
      )['data'] as BudgetForecastDataItem[];
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }

    updatedItems.forEach((item) => {
      item.rollbackAttributes();
      item.wasChangedByPushPayload = true;
    });
    markItemsAsNeedsForceDistribution(updatedItems);

    this.args.onClose();
  });

  get isPrimaryButtonDisabled(): boolean {
    if (this.currentStep === 'fill-from-existing') {
      return this.fillFromExistingTask.isRunning;
    }

    if (this.currentStep === 'custom-bulk-edit') {
      const {
        selectedWeekdays,
        segmentLevel,
        selectedSegmentIds,
        changeType,
        changeValue,
      } = this.customBulkEditOptions;

      const hasNoWeekdaysSelected = selectedWeekdays.length === 0;

      const hasNoSegmentsSelected =
        segmentLevel !== 'totals' && selectedSegmentIds.length === 0;

      const hasNoValidChangeValue =
        changeValue === null ||
        (changeType === 'exact' && changeValue < 0) ||
        (changeType === 'percentage' && changeValue < -100);

      return (
        hasNoWeekdaysSelected || hasNoSegmentsSelected || hasNoValidChangeValue
      );
    }

    return false;
  }

  get modalTitle(): string {
    const base = this.intl.t('general.bulk_update');

    if (this.currentStep === 'select-method') {
      return base;
    }

    return [base, this.intl.t(METHOD_TRANSLATIONS[this.method])].join(' - ');
  }

  get segmentsForView(): BudgetForecastSegment[] {
    return this.args.segments.filter((segment) => segment.visible);
  }

  @action
  goBack(): void {
    this.currentStep = 'select-method';
  }

  @action
  handleNextOrConfirmClick(): void {
    if (this.currentStep === 'select-method') {
      this.currentStep = this.method;
      if (this.method === 'fill-from-existing') {
        this.usageAnalytics.track('Bulk Edit Fill From Existing Clicked', {
          eventTrigger: 'fill-from-existing-clicked',
        });
      } else {
        this.usageAnalytics.track('Custom Bulk Edit Clicked', {
          eventTrigger: 'custom-bulk-edit-clicked',
        });
      }
      return;
    }

    if (this.currentStep === 'fill-from-existing') {
      this.usageAnalytics.track('Bulk Edit Fill From Existing Applied', {
        eventTrigger: 'fill-from-existing-applied',
        dataSource: this.fillFromExistingOptions.dataSource,
      });
      void this.fillFromExistingTask.perform();
      return;
    }

    if (this.currentStep === 'custom-bulk-edit') {
      this.usageAnalytics.track('Custom Bulk Edit Applied', {
        eventTrigger: 'custom-bulk-edit-applied',
      });
      applyBulkEditToItems(
        this.budgetForecast.editableItems,
        this.customBulkEditOptions,
      );
      return this.args.onClose();
    }

    this.currentStep satisfies never;
  }

  @action
  onCustomBulkEditOptionsChange(options: Partial<CustomBulkEditOptions>): void {
    this.customBulkEditOptions = {
      ...this.customBulkEditOptions,
      ...options,
    };
  }

  @action
  onFillFromExistingOptionsChange(
    options: Partial<FillFromExistingOptions>,
  ): void {
    this.fillFromExistingOptions = {
      ...this.fillFromExistingOptions,
      ...options,
    };
  }

  @action
  setMethod(method: Method): void {
    this.method = method;
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.fillFromExistingTask.isRunning}}
      @onClose={{@onClose}}
      @title={{this.modalTitle}}
      class={{styles.modal}}
    >
      <:default>
        <div class="modal--padded modal--small">
          {{#if (eq this.currentStep "select-method")}}
            <SettingsBudgetForecastModalBulkUpdateMethodSelection
              @method={{this.method}}
              @setMethod={{this.setMethod}}
              @view={{@view}}
            />
          {{/if}}

          {{#if (eq this.currentStep "fill-from-existing")}}
            <SettingsBudgetForecastModalBulkUpdateFillFromExisting
              @onOptionsChange={{this.onFillFromExistingOptionsChange}}
              @options={{this.fillFromExistingOptions}}
              @selectedDateRange={{hash
                endDate=@endDate
                granularity=@granularity
                startDate=@startDate
              }}
              @view={{@view}}
            />
          {{/if}}

          {{#if (eq this.currentStep "custom-bulk-edit")}}
            <SettingsBudgetForecastModalBulkUpdateCustomBulkEdit
              @onOptionsChange={{this.onCustomBulkEditOptionsChange}}
              @options={{this.customBulkEditOptions}}
              @segments={{this.segmentsForView}}
              @selectedDateRange={{hash
                endDate=@endDate
                granularity=@granularity
                startDate=@startDate
              }}
            />
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="mx-500 flex h-full items-center justify-end gap-200">
          {{#unless (eq this.currentStep "select-method")}}
            <PrismButton
              @buttonType="ghost"
              @iconBefore="arrow-left"
              @isDisabled={{this.fillFromExistingTask.isRunning}}
              @label={{t "general.back"}}
              @onClick={{this.goBack}}
              class="mr-auto"
            />
          {{/unless}}

          <PrismButton
            @buttonType="secondary"
            @isDisabled={{this.fillFromExistingTask.isRunning}}
            @label={{t "general.cancel"}}
            @onClick={{@onClose}}
          />
          <PrismButton
            @buttonType="primary"
            @iconAfter={{if
              (eq this.currentStep "select-method")
              "arrow-right"
            }}
            @isDisabled={{this.isPrimaryButtonDisabled}}
            @isLoading={{this.fillFromExistingTask.isRunning}}
            @label={{if
              (eq this.currentStep "select-method")
              (t "general.next")
              (t "general.apply")
            }}
            @onClick={{this.handleNextOrConfirmClick}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
